import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import {
  useConfirmProviderPatientAgreementMutation,
  useCreateBecomePatientRequestMutation,
} from 'graphql/generated/hasura';
import AgreementProviderModal from 'components/AgreementProviderModal';
import {
  GetFhirProvidersDocument,
  GetProvidersByPatientIdDocument,
  GetFhirProviderByCodexIdDocument,
  GetFhirPatientbyEmailDocument,
} from 'graphql/generated/remote-schema-hasura';
import { useCustomToast } from 'layout/useToast';
import { usePatientProfileContext } from '../../app/my-account/pages/patient/PatientProfileContext';
import { useProtectedModal } from './ProtectedModalContext';
import { SIGNAL_CHANNELS, useChannelSignal } from 'hooks/useChannelSignal';
import { useAuth } from 'auth/context/AuthContext';
import { useApolloClient } from '@apollo/client';

const PROVIDER_CONNECT_REFETCH_DOCUMENTS = [
  GetFhirProvidersDocument,
  GetProvidersByPatientIdDocument,
  GetFhirProviderByCodexIdDocument,
  GetFhirPatientbyEmailDocument,
];

interface ProviderConnectContextValue {
  isAgreementModalOpen: boolean;
  openAgreementModal: (id: string) => void;
  handlePatientRequest: (id: string) => void;
  closeAgreementModal: () => void;
  handleAccepted: () => void;
  isLoading: boolean;
  requestingProviderId: string | null;
  createPatientRequestLoading: boolean;
}

const ProviderConnectContext = createContext<
  ProviderConnectContextValue | undefined
>(undefined);

export const ProviderConnectContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { user: loggedUser, selectedAccount } = useAuth();
  const { addToast } = useCustomToast();
  const { userValidation } = usePatientProfileContext();
  const { setOpen: setIsOpenProtectModal } = useProtectedModal();

  const client = useApolloClient();

  const refetch = useCallback(() => {
    client.refetchQueries({
      include: PROVIDER_CONNECT_REFETCH_DOCUMENTS,
    });
  }, [client]);

  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [requestingProviderId, setRequestingProviderId] = useState<
    string | null
  >(null);

  const openAgreementModal = useCallback(
    (id: string) => {
      if (!userValidation.generalValidation) {
        setIsOpenProtectModal(true);
        return;
      }
      setIsAgreementModalOpen(true);
      setProviderId(id);
    },
    [setIsOpenProtectModal, userValidation.generalValidation],
  );

  const closeAgreementModal = useCallback(() => {
    setIsAgreementModalOpen(false);
    setProviderId(null);
  }, []);

  const [createPatientRequest, { loading: createPatientRequestLoading }] =
    useCreateBecomePatientRequestMutation({
      awaitRefetchQueries: true,
      refetchQueries: PROVIDER_CONNECT_REFETCH_DOCUMENTS,
      onCompleted: () => {
        setRequestingProviderId(null);
        addToast({
          type: 'success',
          message:
            'Request sent! We’ve notified the provider. You will be able to schedule an appointment as soon as they confirm.',
        });
      },
      onError: (error) => {
        setRequestingProviderId(null);
        addToast({
          type: 'warning',
          message: error.message,
        });
      },
    });

  const handlePatientRequest = (id: string) => {
    if (!userValidation.generalValidation) {
      setIsOpenProtectModal(true);
      return;
    }
    setRequestingProviderId(id);
    return createPatientRequest({ variables: { providerCodexId: id } });
  };

  const [request, { loading: loadingRequest }] =
    useConfirmProviderPatientAgreementMutation({
      variables: {
        providerCodexId: providerId as string,
      },
      refetchQueries: PROVIDER_CONNECT_REFETCH_DOCUMENTS,
      awaitRefetchQueries: true,
      onCompleted: () =>
        addToast({
          type: 'success',
          message: 'Agreement with provider signed successfully',
        }),
    });

  const handleAccepted = useCallback(
    () => request().then(closeAgreementModal),
    [request, closeAgreementModal],
  );

  useChannelSignal(
    (first: boolean) => {
      if (!first) {
        refetch();
      }
    },
    SIGNAL_CHANNELS.PROVIDER_PATIENT_REQUESTS,
    selectedAccount || loggedUser?.uuid,
  );

  const isLoading = loadingRequest;

  return (
    <ProviderConnectContext.Provider
      value={{
        isAgreementModalOpen,
        openAgreementModal,
        handlePatientRequest,
        handleAccepted,
        closeAgreementModal,
        createPatientRequestLoading,
        requestingProviderId,
        isLoading,
      }}
    >
      {children}
      <AgreementProviderModal
        isOpen={isAgreementModalOpen}
        setIsOpen={setIsAgreementModalOpen}
        handleAccepted={handleAccepted}
        loadingAccepted={isLoading}
      />
    </ProviderConnectContext.Provider>
  );
};

export const useProviderConnect = (): ProviderConnectContextValue => {
  const context = useContext(ProviderConnectContext);
  if (!context) {
    throw new Error(
      'useProviderConnect must be used within a ProviderConnectContextProvider',
    );
  }
  return context;
};
